import './SubtaskReports.scss';

import clsx from 'clsx';
import moment from 'moment';
import React, { useMemo } from 'react';

import ScrollableSectonCard from '@/src/components/common/Elements/Card/SectionCard/ScrollableSectonCard/ScrollableSectonCard';
import { useDateFilter } from '@/src/components/common/Elements/DateRangePicker/useDateFilter';
import SectionLoader from '@/src/components/common/Elements/Spinner/SectionLoader/SectionLoader';
import { TaskStatusEnum } from '@/src/components/task/Config';
import { SubtaskTypeEnum } from '@/src/components/task/Details/Tabs/Subtasks/Config';

import { SubtaskReportsResponse, useSubtaskReports } from './api/getSubtaskReports';
import HeaderActions from './HeaderActions/HeaderActions';
import SubtaskReportsList from './SubtaskReportsList/SubtaskReportsList';
import { ResponseSubtaskReportDto, SubtaskReport } from './types';

import { getWebappFileViewerPageUrl } from '../../../Inbox2/pages/InboxEntryViewerPage/FileEntryViewerPage/WebappFileEntryViewerPage';
import { TASK_STATUS_MAPS } from '../../Config';

type ResponseSubtaskReportDtoToMap = Omit<ResponseSubtaskReportDto, 'id'> & {
  id: string | number;
  subTaskId: number;
};

type SubtaskReportsProps = {
  projectId: string;
  isReportsEnabled?: boolean;
};

export const API_DATE_FORMAT = 'YYYY-MM-DD';
const LABEL_DATE_FORMAT = 'DD MMM YYYY';

const getStatusLabel = ({ taskStatus, isDelayed }) => {
  if (isDelayed && taskStatus === TaskStatusEnum.InProgress) {
    return TaskStatusEnum.Delayed;
  }

  return TASK_STATUS_MAPS[taskStatus] || taskStatus;
};

export const getDefaultSubtaskReportProps = (
  rawSubtaskReport: ResponseSubtaskReportDtoToMap
): SubtaskReport => {
  const isDelayed = rawSubtaskReport.task.isDelayed;

  return {
    id: rawSubtaskReport.id,
    taskId: rawSubtaskReport.task.id,
    subTaskId: rawSubtaskReport.subTaskId,
    label: rawSubtaskReport.description,
    type: rawSubtaskReport.type,
    workstreamName: rawSubtaskReport.workstream.workstream,
    workstreamColor: rawSubtaskReport.workstream.color,
    isDelayed,
    status: getStatusLabel({ taskStatus: rawSubtaskReport.task.status, isDelayed }),
    adjustedDate: moment(rawSubtaskReport.task.adjustedDateFormatted).format(LABEL_DATE_FORMAT),
    href: rawSubtaskReport.link,
  };
};

export const mapSubtaskReports = (projectId: string, rawSubtaskReports: SubtaskReportsResponse) => {
  const subtaskReports: SubtaskReport[] = [];

  for (const rawSubtaskReport of rawSubtaskReports.reports) {
    const subTaskReport = {
      ...rawSubtaskReport,
      subTaskId: rawSubtaskReport.id,
    };
    if (
      rawSubtaskReport.type === SubtaskTypeEnum.DOCUMENT &&
      rawSubtaskReport.documents.length > 0
    ) {
      for (const rawSubtaskReportFile of rawSubtaskReport.documents) {
        subtaskReports.push({
          ...getDefaultSubtaskReportProps({
            ...subTaskReport,
            id: `${subTaskReport.id}_${rawSubtaskReportFile.id}`,
            link: getWebappFileViewerPageUrl({
              projectId,
              inboxEntryId: rawSubtaskReportFile.id,
              withBasePath: false,
            }),
          }),
          label: rawSubtaskReportFile.origin,
          rawName: rawSubtaskReportFile.name,
        });
      }
    } else {
      subtaskReports.push({
        ...getDefaultSubtaskReportProps(subTaskReport),
      });
    }
  }

  return subtaskReports;
};

const SubtaskReports = ({ projectId, isReportsEnabled }: SubtaskReportsProps) => {
  const { dateFilter, onDateFilterChange, onResetDateFilter, onPreviousMonth, onNextMonth } =
    useDateFilter();
  const subtaskReportsQuery = useSubtaskReports({
    projectId,
    from: dateFilter.from.format(API_DATE_FORMAT),
    to: dateFilter.to.format(API_DATE_FORMAT),
  });

  const mappedSubtaskReports = useMemo(() => {
    if (subtaskReportsQuery.data) {
      return mapSubtaskReports(projectId, subtaskReportsQuery.data);
    }
    return null;
  }, [projectId, subtaskReportsQuery.data]);

  if (subtaskReportsQuery.isLoading || !subtaskReportsQuery.data) {
    return (
      <SectionLoader
        className={clsx(
          'container--reports',
          'tw-max-w-full tw-h-[calc(100%-5px)] tw-w-full tw-mr-[20px]',
          isReportsEnabled ? 'tw-max-h-[120px]' : 'tw-max-h-[inherit]'
        )}
      />
    );
  }

  return (
    <ScrollableSectonCard
      className={clsx(
        'container--reports',
        'tw-w-full',
        'tw-min-w-0',
        'tw-mr-[20px]',
        '!tw-pb-[12px]'
      )}
      scrollClasses={clsx('tw-h-[calc(100%-1px)]', 'tw-w-full', 'tw-min-w-0')}
      scrollOffset={260}
      scrollDirection="scrollX"
      sectionKey={`ssc_${dateFilter.from.format(API_DATE_FORMAT)}-${dateFilter.to.format(API_DATE_FORMAT)}`}
      isReportsEnabled={isReportsEnabled}
    >
      {/* Header prev/next month navigation & calendar filter */}
      <HeaderActions
        dateFilter={dateFilter}
        onDateFilterChange={onDateFilterChange}
        onResetDateFilter={onResetDateFilter}
        onNextMonth={onNextMonth}
        onPreviousMonth={onPreviousMonth}
      />

      {/* Tiles for the reports files/subtask */}
      <SubtaskReportsList
        subtaskReports={mappedSubtaskReports}
        isReportsEnabled={isReportsEnabled}
      />
    </ScrollableSectonCard>
  );
};

export default SubtaskReports;
